import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../Header/PageHeader';
import TabView from '../tabView/TabView';
import DEFAULT_ROUTES from '../../constants/routes';
import { setActivePage } from '../../features/navigation/navigationSlice';
import { showSnackbar } from '../../features/snakbar/snackbarSlice';
import DeliveryLogTab from './DeliveryLogTab';
import WebhookConfigTab from './WebhookConfigTab';
import SlackChannelConfigTab from './SlackChannelConfigTab';
import {
  PROJECT_SETTINGS_PAGE_COPY,
  PROJECT_SETTINGS_TABS,
} from '../notifications/notification.constants';
import PermissionEnum from '../../enums/PermissionEnum';
import styles from './ProjectNotificationSettings.module.scss';

const PAGE_COPY = {
  ...PROJECT_SETTINGS_PAGE_COPY,
  tabs: PROJECT_SETTINGS_TABS,
};

const canManageProjectSettings = (user, projectId) => {
  if (!user || !projectId) return false;
  const globalPermissions = user.permissions || [];
  const projectPermissions = user.project_roles_permissions?.consolidatedPermissions?.[projectId];
  return globalPermissions.includes(PermissionEnum.MANAGE_PROJECT_SETTING.name)
    || Boolean(projectPermissions?.includes(PermissionEnum.MANAGE_PROJECT_SETTING.name));
};

const ProjectNotificationSettings = () => {
  const { selectedProject } = useSelector((state) => state.projects);
  const { activePage } = useSelector((state) => state.navigation);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectId = selectedProject?.id;
  const canManageSettings = canManageProjectSettings(user, projectId);

  useEffect(() => {
    if (!projectId || canManageSettings) return;

    dispatch(showSnackbar({
      message: PAGE_COPY.accessDenied,
      severity: 'warning',
      duration: 4000,
    }));
    navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}`, { replace: true });
  }, [canManageSettings, dispatch, navigate, projectId]);

  const breadcrumbItems = useMemo(() => ([
    {
      label: 'Project',
      onClick: () => {
        dispatch(setActivePage('Projects'));
        navigate(DEFAULT_ROUTES.BASE_URL);
      },
    },
    {
      label: selectedProject?.name || PAGE_COPY.projectFallback,
      onClick: () => {
        dispatch(setActivePage('Test Cases'));
        navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${projectId}/test-cases`);
      },
    },
    { label: activePage },
  ]), [activePage, dispatch, navigate, projectId, selectedProject?.name]);

  const tabsData = useMemo(() => [
    {
      name: PAGE_COPY.tabs.webhook,
      component: <WebhookConfigTab projectId={projectId} />,
    },
    {
      name: PAGE_COPY.tabs.deliveryLog,
      component: <DeliveryLogTab projectId={projectId} />,
    },
    {
      name: PAGE_COPY.tabs.slackChannelConfig,
      component: <SlackChannelConfigTab projectId={projectId} />,
    },
  ], [projectId]);

  if (!canManageSettings) return null;

  return (
    <div className={styles.page}>
      <PageHeader
        projectName={selectedProject?.name}
        title={PAGE_COPY.title}
        description={PAGE_COPY.description}
        activePage={activePage}
        breadcrumbItems={breadcrumbItems}
        actions={[]}
      />
      <div className={styles.tabContainer}>
        <TabView tabsData={tabsData} />
      </div>
    </div>
  );
};

export default ProjectNotificationSettings;
